import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, AlertCircle, Loader2, ShieldAlert } from 'lucide-react';
import { getDriverComplianceStatus, submitDriverCompliance } from '../services/api';

/**
 * Wraps the driver dashboard. If the driver's compliance_status is anything
 * other than "active", shows a full-screen modal with the compliance form.
 *
 * - "restricted" (existing driver missing data) → must complete to continue.
 * - "pending_review" (form submitted, waiting on admin) → shows a friendly waiting screen.
 * - "rejected" → shows rejection note + lets them re-submit.
 */
export default function ComplianceGate({ user, children }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    vehicle_type: '',
    licence_file: '',
    licence_file_back: '',
    insurance_confirmed: false,
    age_confirmed: false,
    terms_accepted: false,
    vat_registered: false,
    vat_number: ''
  });

  const refresh = async () => {
    try {
      const { data } = await getDriverComplianceStatus();
      setStatus(data);
      setForm(f => ({
        ...f,
        vehicle_type: data.vehicle_type || f.vehicle_type,
        vat_registered: !!data.vat_registered,
        vat_number: data.vat_number || ''
      }));
    } catch (e) {
      setStatus({ compliance_status: 'restricted', missing_fields: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'driver') refresh();
    else setLoading(false);
  }, [user]);

  const handleLicence = (e, side = 'front') => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Licence image must be under 5 MB.'); return; }
    if (!file.type.startsWith('image/')) { setError('Please upload a JPG or PNG.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const key = side === 'back' ? 'licence_file_back' : 'licence_file';
      setForm(p => ({ ...p, [key]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.vehicle_type) return setError('Please select your vehicle type.');
    if (!status?.licence_uploaded && !form.licence_file) return setError('Please upload a clear photo of your licence (front).');
    if (!form.insurance_confirmed) return setError('Please confirm your insurance covers delivery work.');
    if (!form.age_confirmed) return setError('Please confirm you are over 18 and licensed in Ireland.');
    if (!form.terms_accepted) return setError('Please accept the Terms & Agreement.');
    if (form.vat_registered && !form.vat_number.trim()) return setError('Please enter your VAT number, or set "VAT registered" to No.');
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!form.licence_file) delete payload.licence_file; // server will keep existing file
      if (!form.licence_file_back) delete payload.licence_file_back;
      await submitDriverCompliance(payload);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Non-driver, or active driver — render dashboard untouched
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#2E7D32]" /></div>;
  }
  if (user?.role !== 'driver' || status?.compliance_status === 'active') {
    return children;
  }

  // Pending review (admin hasn't approved yet)
  if (status?.compliance_status === 'pending_review') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="compliance-pending-screen">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
          <CheckCircle className="w-10 h-10 text-[#2E7D32] mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-1">Thanks — your details are being reviewed</h2>
          <p className="text-sm text-gray-600">Our team is reviewing your licence and details. This usually takes a few hours during business hours. You'll be able to bid on jobs as soon as you're approved.</p>
          <p className="text-xs text-gray-400 mt-4">If you spot a mistake, refresh after re-submission.</p>
        </div>
      </div>
    );
  }

  // Restricted or rejected — show form
  const showRejection = status?.compliance_status === 'rejected';

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6" data-testid="compliance-gate-form">
      <div className="max-w-lg mx-auto bg-white border border-gray-200 rounded-xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">Quick compliance check (under 2 minutes)</h2>
            <p className="text-sm text-gray-600 mt-1">
              We've updated our compliance requirements. Please complete this quick check to continue using JustDeliver. Your licence will be reviewed before activation.
            </p>
          </div>
        </div>

        {showRejection && status?.licence_review_note && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-700 mt-0.5" />
            <p className="text-xs text-red-800"><span className="font-semibold">Admin note:</span> {status.licence_review_note}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2" data-testid="compliance-error">
            <AlertCircle className="w-4 h-4 text-red-700 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Vehicle */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Vehicle Type *</Label>
            <div className="flex gap-2">
              {['Car', 'Van', 'Truck'].map(v => {
                const val = v.toLowerCase();
                const sel = form.vehicle_type === val;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, vehicle_type: val }))}
                    data-testid={`gate-vehicle-${val}`}
                    className={`flex-1 py-2 text-sm rounded-md border ${sel ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32]'}`}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Licence — front + back */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Driving Licence — Front {!status?.licence_uploaded && '*'}</Label>
            <label
              htmlFor="gate-licence-input"
              className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#2E7D32] text-sm text-gray-600 hover:text-[#2E7D32]"
              data-testid="gate-licence-label"
            >
              {form.licence_file || status?.licence_uploaded ? (
                <>
                  <CheckCircle className="w-4 h-4 text-[#2E7D32]" />
                  <span className="text-[#2E7D32] font-medium">{form.licence_file ? 'New front selected' : 'Front on file'} — tap to replace</span>
                </>
              ) : (
                <span>Upload the FRONT of your licence (JPG/PNG, ≤5 MB)</span>
              )}
            </label>
            <input
              id="gate-licence-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleLicence(e, 'front')}
              data-testid="gate-licence-input"
            />

            <Label className="text-xs text-gray-500 pt-1 block">Driving Licence — Back <span className="text-gray-400 font-normal">(shows vehicle categories)</span></Label>
            <label
              htmlFor="gate-licence-back-input"
              className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#2E7D32] text-sm text-gray-600 hover:text-[#2E7D32]"
              data-testid="gate-licence-back-label"
            >
              {form.licence_file_back || status?.licence_back_uploaded ? (
                <>
                  <CheckCircle className="w-4 h-4 text-[#2E7D32]" />
                  <span className="text-[#2E7D32] font-medium">{form.licence_file_back ? 'New back selected' : 'Back on file'} — tap to replace</span>
                </>
              ) : (
                <span>Upload the BACK of your licence (JPG/PNG, ≤5 MB)</span>
              )}
            </label>
            <input
              id="gate-licence-back-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleLicence(e, 'back')}
              data-testid="gate-licence-back-input"
            />
            <p className="text-[11px] text-gray-500">Tap each box to use your camera or pick from your gallery.</p>
          </div>

          <div className="p-3 bg-white border border-indigo-200 rounded-md">
            <div className="text-sm font-semibold text-indigo-900 mb-1">Driver Insurance Declaration</div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#2E7D32]"
                checked={form.insurance_confirmed}
                onChange={(e) => setForm(p => ({ ...p, insurance_confirmed: e.target.checked }))}
                data-testid="gate-insurance-checkbox" />
              <span className="text-xs text-gray-700 leading-relaxed">
                I confirm that I hold, and will maintain, all insurance required by law to carry out paid delivery work in Ireland. I understand that I am solely responsible for ensuring my insurance remains valid and appropriate for the work I undertake through JustDeliver. I also understand that JustDeliver may request proof of insurance at any time, and failure to provide it may result in my account being suspended or terminated.
              </span>
            </label>
            <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
              JustDeliver is a technology platform connecting independent drivers with customers. Drivers are responsible for ensuring they have the appropriate insurance required for the services they provide.
            </p>
          </div>

          <label className="flex items-start gap-2 p-2 bg-white border border-gray-200 rounded-md cursor-pointer">
            <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#2E7D32]"
              checked={form.age_confirmed}
              onChange={(e) => setForm(p => ({ ...p, age_confirmed: e.target.checked }))}
              data-testid="gate-age-checkbox" />
            <span className="text-xs text-gray-700">
              I confirm I am over 18 and legally permitted to drive in Ireland.
            </span>
          </label>

          <label className="flex items-start gap-2 p-2 bg-white border border-gray-200 rounded-md cursor-pointer">
            <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#2E7D32]"
              checked={form.terms_accepted}
              onChange={(e) => setForm(p => ({ ...p, terms_accepted: e.target.checked }))}
              data-testid="gate-terms-checkbox" />
            <span className="text-xs text-gray-700">
              <span className="font-semibold">I agree to the Terms & Agreement.</span>
              <span className="block text-[11px] text-gray-500 mt-1 leading-relaxed">
                • Drivers are self-employed (independent contractors).<br />
                • Drivers are responsible for their own licence, insurance and taxes.<br />
                • JustDeliver acts as a platform connecting customers with drivers.<br />
                • JustDeliver is not liable for driver actions during deliveries.<br />
                • JustDeliver may remove drivers for repeated poor performance or complaints.
              </span>
            </span>
          </label>

          {/* VAT */}
          <div className="space-y-1.5 p-2 bg-white border border-gray-200 rounded-md">
            <Label className="text-xs text-gray-700 font-medium">Are you VAT registered?</Label>
            <div className="flex gap-2">
              {[{ k: false, l: 'No' }, { k: true, l: 'Yes' }].map(({ k, l }) => {
                const sel = form.vat_registered === k;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, vat_registered: k, vat_number: k ? p.vat_number : '' }))}
                    data-testid={`gate-vat-${l.toLowerCase()}`}
                    className={`flex-1 py-1.5 text-sm rounded-md border ${sel ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32]'}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
            {form.vat_registered && (
              <Input
                placeholder="VAT number (e.g. IE1234567X)"
                value={form.vat_number}
                onChange={(e) => setForm(p => ({ ...p, vat_number: e.target.value.toUpperCase() }))}
                className="rounded-sm text-sm"
                data-testid="gate-vat-number-input"
              />
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white"
            data-testid="gate-submit-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : 'Submit for review'}
          </Button>
          <p className="text-[11px] text-gray-500 text-center">
            Until you're approved, you cannot bid on jobs. Reviews usually take a few hours.
          </p>
        </form>
      </div>
    </div>
  );
}
